import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, ArrowRight, Users,
  Music, GraduationCap, BookOpen, Palette, Bot, CalendarDays, Flower2, PersonStanding, Volleyball } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BannerCarousel } from "@/components/BannerCarousel";
import { PromoCards } from "@/components/PromoCards";
import { ActivityCard } from "@/components/ActivityCard";
import { LeftSidebar, RightSidebar } from "@/components/HomeSidebars";
import { Button } from "@/components/ui/button";
import { AUDIENCES, SUBCATEGORIES, SPORTS, CITIES } from "@/constants/categories";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const HERO = {
  paidia: {
    img: "/brand/home_paidia_card.jpg",
    full: "/brand/home_card_paidia_v2.jpg",
    overlay: "from-sky-950/90 via-sky-900/30",
    ring: "hover:ring-sky-400/60",
    descEl: "Δραστηριότητες, μαθήματα και εκδηλώσεις για κάθε ηλικία",
    descEn: "Activities, lessons and events for every age",
  },
  enilikes: {
    img: "/brand/home_enilikes_card.jpg",
    full: "/brand/home_card_enilikes_v2.jpg",
    overlay: "from-emerald-950/90 via-emerald-900/30",
    ring: "hover:ring-emerald-400/60",
    descEl: "Δραστηριότητες, εκπαίδευση και επιλογές για κάθε ενδιαφέρον",
    descEn: "Activities, education and options for every interest",
  },
};

const AudienceCard = ({ audience, delay }) => {
  const navigate = useNavigate();
  const { tt, lang } = useLang();
  const a = AUDIENCES[audience];
  const h = HERO[audience];
  return (
    <button
      data-testid={`home-audience-${audience}`}
      onClick={() => navigate(`/c/${audience}`)}
      className={`group relative flex-1 min-w-0 rounded-[1.75rem] overflow-hidden ring-1 ring-black/5 ${h.ring} hover:ring-2 shadow-lg hover:shadow-2xl transition-[box-shadow,transform] duration-300 hover:-translate-y-1 text-left`}
    >
      {lang !== "en" ? (
        <img src={h.full} alt={tt(a)} loading="eager" decoding="sync" className="block w-full h-auto group-hover:scale-[1.02] transition-transform duration-500" />
      ) : (<>
      <div className="aspect-[5/4]">
        <img src={h.img} alt={tt(a)} loading="eager" decoding="sync" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className={`absolute inset-0 bg-gradient-to-t ${h.overlay} to-transparent`} />
      <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6 flex items-end justify-between gap-3">
        <div className="text-white">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold drop-shadow">{tt(a)}</h2>
          <p className="mt-1 text-sm sm:text-base text-white/90 max-w-[22rem] leading-snug">{lang === "en" ? h.descEn : h.descEl}</p>
        </div>
        <span className="shrink-0 h-11 w-11 grid place-items-center rounded-full bg-white text-slate-900 shadow-md group-hover:scale-110 transition-transform"><ArrowRight size={20} /></span>
      </div>
      </>)}
    </button>
  );
};

const POPULAR = [
  { key: "athlitikoi", img: "/popular/athlitikoi.jpg", to: "/sports/paidia" },
  { key: "sxoles_xorou", img: "/popular/sxoles_xorou.jpg", to: "/s/paidia/sxoles_xorou" },
  { key: "xenes_glosses", img: "/popular/xenes_glosses.jpg", to: "/s/paidia/xenes_glosses" },
  { key: "kentra_meletis", img: "/popular/kentra_meletis.jpg", to: "/s/paidia/kentra_meletis" },
  { key: "sxoles_mousikis", img: "/popular/sxoles_mousikis.jpg", to: "/s/paidia/sxoles_mousikis" },
  { key: "eikastikes", img: "/popular/eikastikes.jpg", to: "/s/paidia/eikastikes" },
  { key: "stem", img: "/popular/stem.jpg", to: "/s/paidia/stem" },
  { key: "__paidia", img: "/popular/paidia.jpg", to: "/c/paidia" },
  { key: "xoroi_ekdiloseon", img: "/popular/xoroi_ekdiloseon.jpg", to: "/s/paidia/xoroi_ekdiloseon" },
  { key: "__enilikes", img: "/popular/enilikes.jpg", to: "/c/enilikes" },
];

const selectCls = "h-12 px-4 rounded-xl bg-white border border-slate-200 font-semibold text-sm text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 cursor-pointer";

const Home = () => {
  const { t, tt, lang } = useLang();
  const navigate = useNavigate();
  const resultsRef = useRef(null);
  const [banners, setBanners] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [city, setCity] = useState("");
  const [activity, setActivity] = useState("");
  const [sport, setSport] = useState("");
  const [age, setAge] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    api.get("/banners-active").then((r) => setBanners(r.data)).catch(() => {});
    api.get("/promotions/active").then((r) => setPromotions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!city && !activity && !age) { setResults([]); setSearched(false); return; }
    const params = { sort: "rating" };
    if (city) params.city = city;
    if (activity) params.subcategory = activity;
    if (activity === "athlitikoi" && sport) params.sport = sport;
    api.get("/businesses", { params }).then((r) => {
      let data = r.data;
      if (age) data = data.filter((b) => (b.audiences || []).includes(age));
      setResults(data); setSearched(true);
    });
  }, [city, activity, sport, age]);

  const runSearch = () => {
    setSearched(true);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const catLabel = (p) => p.key === "__paidia" ? tt(AUDIENCES.paidia) : p.key === "__enilikes" ? tt(AUDIENCES.enilikes) : tt(SUBCATEGORIES[p.key]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar large />

      <div className="xl:grid xl:grid-cols-[290px_minmax(0,1fr)_290px] 2xl:grid-cols-[330px_minmax(0,1fr)_330px] xl:gap-4 xl:px-4 max-w-[1920px] mx-auto">
      <aside className="hidden xl:block pt-4">
        <div className="sticky top-24"><LeftSidebar /></div>
      </aside>
      <div className="min-w-0">
      {/* HERO */}
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-sky-50 via-white to-white" />
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-sky-200/40 blur-3xl" />
        <div className="pointer-events-none absolute -top-10 right-0 h-72 w-72 rounded-full bg-emerald-200/40 blur-3xl" />
        {/* City skyline artwork (left & right behind hero cards) */}
        <img src="/brand/city_skyline.png" alt="" aria-hidden="true" style={{ WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 45%)", maskImage: "linear-gradient(to bottom, transparent 0%, black 45%)" }} className="pointer-events-none select-none absolute bottom-0 left-0 w-full h-auto opacity-50 object-cover" />
        <main className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
          {/* decorative brush strokes (in the side margins, lg+ only) */}
          <svg className="pointer-events-none hidden lg:block absolute left-2 top-16 w-20 h-7 text-sky-400" viewBox="0 0 120 40" fill="none"><path d="M4 30 C40 8, 80 8, 116 24" stroke="currentColor" strokeWidth="7" strokeLinecap="round"/></svg>
          <svg className="pointer-events-none hidden lg:block absolute right-2 top-10 w-20 h-7 text-emerald-400" viewBox="0 0 120 40" fill="none"><path d="M4 20 C40 4, 80 4, 116 30" stroke="currentColor" strokeWidth="7" strokeLinecap="round"/></svg>
          <div className="relative text-center max-w-3xl mx-auto mb-6">
            <h1 className="font-display italic text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#132a52]">{lang === "en" ? t("home.title") : (<>Βρες αυτό που <span className="bg-gradient-to-r from-[#0B5ED7] to-[#18A0FB] bg-clip-text text-transparent pr-1">σου ταιριάζει</span></>)}</h1>
            <p className="mt-4 text-lg text-slate-600">{t("home.subtitle")}</p>
          </div>

          {/* QUICK SEARCH */}
          <section className="max-w-5xl mx-auto w-full" data-testid="home-filter-bar">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_10px_40px_-15px_rgba(2,132,197,0.25)] p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-3 text-slate-500 text-sm font-bold"><SlidersHorizontal size={16} /> {t("home.quickSearch")}</div>
              <div className="flex flex-col lg:flex-row gap-3">
                <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="home-city-select" className={`${selectCls} flex-1 min-w-0`}>
                  <option value="">{t("home.allCities")}</option>
                  {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={activity} onChange={(e) => { setActivity(e.target.value); if (e.target.value !== "athlitikoi") setSport(""); }} data-testid="home-activity-select" className={`${selectCls} flex-1 min-w-0`}>
                  <option value="">{t("home.allActivities")}</option>
                  {Object.values(SUBCATEGORIES).map((s) => <option key={s.slug} value={s.slug}>{tt(s)}</option>)}
                </select>
                <select value={sport} onChange={(e) => { setSport(e.target.value); if (e.target.value) setActivity("athlitikoi"); }} data-testid="home-sport-select" className={`${selectCls} flex-1 min-w-0`}>
                  <option value="">{t("home.allSports")}</option>
                  {Object.values(SPORTS).map((s) => <option key={s.slug} value={s.slug}>{tt(s)}</option>)}
                </select>
                <Button onClick={runSearch} data-testid="home-search-button" className="h-12 px-6 rounded-xl bg-primary text-white font-bold gap-2 shrink-0"><Search size={18} /> {lang === "en" ? "Search" : "Αναζήτηση"}</Button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
            <AudienceCard audience="paidia" delay={0.1} />
            <AudienceCard audience="enilikes" delay={0.2} />
            </div>


            {promotions.length > 0 && <div className="mt-8"><PromoCards promotions={promotions} /></div>}

            {searched && (
              <div ref={resultsRef} className="mt-6 scroll-mt-24" data-testid="home-results">
                <h3 className="font-display text-xl font-bold text-slate-900 mb-4">
                  {t("home.results")} {results.length > 0 && <span className="text-slate-500 font-semibold">({results.length})</span>}
                </h3>
                {results.length === 0 ? (
                  <p className="text-slate-500">{t("home.noResults")}</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {results.map((b, i) => <ActivityCard key={b.id} business={b} index={i} />)}
                  </div>
                )}
              </div>
            )}
          </section>
        </main>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
        {/* POPULAR CATEGORIES */}
        <section data-testid="home-popular-categories">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-2xl font-extrabold text-slate-900">{lang === "en" ? "Popular categories" : "Δημοφιλείς κατηγορίες"}</h3>
            <Link to="/c/paidia" data-testid="home-see-all-categories" className="text-sm font-bold text-primary inline-flex items-center gap-1 hover:gap-2 transition-all">{lang === "en" ? "See all categories" : "Δείτε όλες τις κατηγορίες"} <ArrowRight size={16} /></Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 sm:grid sm:grid-cols-5 lg:grid-cols-10 sm:overflow-visible">
            {POPULAR.map((p) => (
              <button key={p.key} onClick={() => navigate(p.to)} data-testid="home-category-card" className="group shrink-0 w-24 sm:w-auto flex flex-col items-center text-center gap-2.5">
                <img src={p.img} alt="" className="h-20 w-20 sm:h-[88px] sm:w-[88px] rounded-2xl object-cover shadow-sm group-hover:scale-105 group-hover:-translate-y-0.5 transition-transform" />
                <span className="text-[13px] font-bold text-[#0B2454] leading-tight line-clamp-2">{catLabel(p)}</span>
              </button>
            ))}
          </div>
        </section>

        {/* APP DOWNLOAD */}
        <section data-testid="home-app-badges">
          <div className="rounded-3xl bg-gradient-to-r from-sky-50 to-white border border-sky-100 shadow-sm px-6 sm:px-10 py-6 grid md:grid-cols-[1.2fr_auto_auto] items-center gap-6 md:gap-10">
            <div className="text-center md:text-left">
              <h3 className="font-display italic text-2xl sm:text-3xl font-extrabold leading-tight text-slate-900">
                {lang === "en"
                  ? <><span className="text-slate-900">Skill</span><span className="text-primary">City</span> always with you!</>
                  : <>Το <span className="text-slate-900">Skill</span><span className="text-primary">City</span> πάντα μαζί σου!</>}
              </h3>
              <p className="mt-2 text-slate-600 max-w-sm text-sm sm:text-base">{t("home.appSubtitle")}</p>
              <div className="mt-4 flex items-center justify-center md:justify-start gap-3 flex-wrap">
                <a href="https://apps.apple.com/gr/app/skillcity-app/id6803484920" target="_blank" rel="noopener noreferrer" data-testid="appstore-badge-link" className="transition-transform hover:scale-105">
                  <img src="/badges/appstore.jpg" alt="Download on the App Store" className="h-11 w-auto rounded-lg" />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.geoskl7.skillcity&pli=1" target="_blank" rel="noopener noreferrer" data-testid="googleplay-badge-link" className="transition-transform hover:scale-105">
                  <img src="/badges/googleplay.png" alt="Get it on Google Play" className="h-11 w-auto" />
                </a>
              </div>
            </div>
            {/* Phone mockup (from app design) */}
            <div className="hidden md:flex justify-self-center items-center">
              <img src="/brand/app_phone.png" alt="SkillCity app" className="h-52 w-auto drop-shadow-xl" />
            </div>
            <div className="flex flex-col items-center text-center shrink-0" data-testid="home-qr-block">
              <img src="/badges/qr.jpg" alt="QR code SkillCity" data-testid="home-qr-image" className="h-24 w-24 sm:h-28 sm:w-28 rounded-xl shadow-md border border-slate-200" />
              <p className="mt-2 text-xs text-slate-600 max-w-[11rem]">{t("home.qrText")}</p>
            </div>
          </div>
        </section>

        {/* PROMOTIONAL BANNERS */}
        {banners.length > 0 && <BannerCarousel banners={banners} />}

      </main>
      </div>
      <aside className="hidden xl:block pt-4">
        <div className="sticky top-24"><RightSidebar /></div>
      </aside>
      </div>
      <Footer />
    </div>
  );
};

export default Home;
